import type { StoryElement, TopicContextSource } from '../types';
import { safeHref } from './html';
import { linkHost } from './linkHost';
import { readerLinkUrl } from './storyLinks';

type Alternate = NonNullable<StoryElement['alternatives']>[number];
type SourceStory = Pick<StoryElement, 'id' | 'title' | 'url' | 'linked_url' | 'canonical_domain' | 'alternatives' | 'topic_context'>;
export type ReaderTopicSource = Alternate & { via_url?: string; via_domain?: string };

export interface TopicContextSourceLink {
  label: string;
  href?: string;
  articleId?: string;
}

/** Collapse URL spelling differences, retaining query parameters that identify articles. */
function destinationKey(value: string): string {
  const href = safeHref(value);
  if (!href) return '';
  const url = new URL(href);
  url.hash = '';
  url.hostname = linkHost(href);
  url.pathname = url.pathname.replace(/\/+$/, '') || '/';
  for (const key of [...url.searchParams.keys()]) {
    if (key.startsWith('utm_') || key === 'fbclid' || key === 'gclid') url.searchParams.delete(key);
  }
  url.searchParams.sort();
  return url.toString();
}

function memberSource(member: Partial<Alternate>): ReaderTopicSource | undefined {
  const url = safeHref(member.source_url || readerLinkUrl(member));
  if (!url) return undefined;
  const via = member.url && destinationKey(url) !== destinationKey(member.url);
  return {
    ...member,
    id: member.id || '',
    title: member.title || '',
    url,
    canonical_domain: linkHost(url),
    ...(via ? { via_url: safeHref(member.url), via_domain: member.canonical_domain } : {}),
  };
}

function citedSource(source: TopicContextSource): ReaderTopicSource | undefined {
  const url = safeHref(source.url);
  return url ? {
    id: source.article_id, title: source.title, url, canonical_domain: linkHost(url),
    ...(source.published_at ? { published_at: source.published_at } : {}),
    via_url: safeHref(source.via_url), via_domain: source.via_domain,
  } : undefined;
}

/** Shared source inventory: loaded members plus exact evidence saved with the bullets. */
function sourceInventory(story: Partial<SourceStory>, references: TopicContextSource[] = []): ReaderTopicSource[] {
  const members = [memberSource(story), ...(story.alternatives || []).map(memberSource)];
  const citations = [
    ...references,
    ...(story.topic_context || []).flatMap(category => category.facts.flatMap(fact => fact.source_links || [])),
  ].map(citedSource);
  const byDestination = new Map<string, ReaderTopicSource>();
  for (const source of [...members, ...citations]) {
    if (!source) continue;
    const key = destinationKey(source.url);
    const previous = byDestination.get(key);
    // A first-class publisher row supplies the real headline and tracking ID.
    if (!previous || (previous.via_url && !source.via_url)) byDestination.set(key, source);
  }
  return [...byDestination.values()];
}

/** Publisher entries for “More on this topic”, excluding the story already shown. */
export function readerTopicSources(story: SourceStory): ReaderTopicSource[] {
  const primary = memberSource(story);
  return sourceInventory(story).filter(source => !primary || destinationKey(source.url) !== destinationKey(primary.url));
}

/** Resolve old domain-only facts only when unambiguous; never invent a homepage citation. */
export function topicContextSourceLinks(
  story: Partial<SourceStory>, sources: string[], references?: TopicContextSource[],
): TopicContextSourceLink[] {
  const inventory = sourceInventory(story, references);
  if (references !== undefined) {
    const seen = new Set<string>();
    return references.flatMap(reference => {
      const key = destinationKey(reference.url);
      if (!key || seen.has(key)) return [];
      seen.add(key);
      const member = inventory.find(source => destinationKey(source.url) === key)!;
      return [{ label: linkHost(member.url), href: member.url, articleId: member.id }];
    });
  }
  return sources.map(label => {
    const host = linkHost(label.includes('://') ? label : `https://${label}`).toLowerCase();
    const matches = inventory.filter(source => source.canonical_domain.toLowerCase() === host);
    const member = matches.length === 1 ? matches[0] : undefined;
    return { label, ...(member ? { href: member.url, articleId: member.id || story.id } : {}) };
  });
}

/** Prefix for a bullet's citations; none when a one-article story would only cite itself. */
export function topicContextSourcesLabel(
  links: TopicContextSourceLink[], storyId: string, singleArticle: boolean,
): string | undefined {
  if (links.length === 0 || (singleArticle && links.every(link => link.articleId === storyId))) return undefined;
  return links.length === 1 ? 'Source' : 'Sources';
}
